/**
 * @fileoverview Ticking sessions in the organization's lists and acting on
 * them together: the dashboard's Lessons tab, a program's Sessions tab and a
 * class's Lessons tab.
 *
 * Every row has a tick box, whatever became of the session, and once some are
 * ticked the bar above the list offers what fits them, each with the count it
 * will reach:
 *
 * - **Clear**, **Decline** and **Restore** the missing-notes notices among
 *   them (useNotesClearActions), as before.
 * - **Export** every ticked row as CSV, in the columns the tab's own export
 *   writes (lib/orgSessions `sessionsCsv`), so a ticked handful and the whole
 *   tab come out alike. Built from the rows on screen: nothing leaves that the
 *   list did not already show.
 * - **Archive** the sessions that have happened: they leave the
 *   organization's lists and wait under the Archived status, where **Restore**
 *   brings them back. They still count in every number the organization sees.
 * - **Delete** them: off the organization's lists, and out of its numbers,
 *   for good.
 *
 * A session that owed notes has its notice cleared by either. Neither touches
 * an instructor's notes, the class's schedule, teaching records or billing
 * (backend services/org_session_marks). A session still to come can be
 * exported but not archived or deleted: it has not happened yet.
 *
 * Select all ticks every row the filters chose, the ones under "Show more"
 * included.
 */

import { useMemo, useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import useRowSelection from "@/hooks/useRowSelection";
import { NotesClearDialog, NotesClearToolbar, SelectBox } from "@/components/studio/NotesClearing";
import { Icon } from "@/components/studio/primitives";
import { saveBlob, CSV_TYPE } from "@/components/studio/ExportMenu";
import { notesActionable, useNotesClearActions } from "@/components/org/useOrgNotesClear";
import { isCleared, plural, sendInPages, toastResult } from "@/lib/notesClearance";
import { lessonStatus } from "@/lib/lessonHistory";
import { sessionsCsv } from "@/lib/orgSessions";

/* What became of a row that lets it be archived or deleted: it has happened,
   or it will not. Read through `lessonStatus`, which every list's rows answer
   (`sessionRow` and a class page's own). */
const SETTLED = new Set(["written", "missing", "cleared", "cancelled"]);

/** Whether a row can be archived or deleted: settled, and named by the server
    (`session_key`: its meeting, or its lesson). */
const settled = (row) => Boolean(row.session_key) && SETTLED.has(lessonStatus(row));

/** What the Archived view says about the numbers: archived sessions are
    counted everywhere, deleted ones nowhere (docs/org-session-archive.md). */
export const ARCHIVE_COUNTS_NOTE =
  "Archived sessions still count in your dashboard's numbers, reports and exports. Delete a session to stop counting it.";

/** The warning over a list's Archived view: what archiving does not do. */
export function ArchivedNote({ className = "mb-3", testId = "archived-note" }) {
  return (
    <p
      className={`flex items-start gap-2 font-sans text-[13px] leading-relaxed text-[var(--text-secondary)] ${className}`}
      data-testid={testId}
    >
      <Icon name="info" size={15} className="mt-[2px] text-[var(--accent-yellow-text)]" />
      {ARCHIVE_COUNTS_NOTE}
    </p>
  );
}

const PATHS = {
  archive: ["/org/sessions/archive", "archived"],
  restore: ["/org/sessions/restore", "restored"],
  delete: ["/org/sessions/delete", "deleted"],
};

/**
 * @param {{
 *   rows: Array<object>, onChanged?: () => void,
 *   keyOf?: (row: object) => string, testId?: string,
 *   summary?: any, exportName?: string, toExportRow?: (row: object) => object,
 * }} options `rows` is every row the filters chose, oldest first; `summary`
 *   stands in the bar while nothing is ticked (the list's count);
 *   `exportName` names the file; `toExportRow` maps a list's own row shape onto
 *   the export's fields.
 */
export default function useOrgSessionActions({
  rows,
  onChanged,
  keyOf = (r) => r.lesson_id,
  testId = "org-sessions",
  summary,
  exportName = "hans-lessons-selected",
  toExportRow,
}) {
  const byKey = useMemo(() => new Map(rows.map((r) => [keyOf(r), r])), [rows, keyOf]);
  const selection = useRowSelection(rows.map(keyOf));
  const chosen = [...selection.selected].map((k) => byKey.get(k)).filter(Boolean);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const notes = useNotesClearActions({ chosen, onDone: selection.clear, onChanged, testId });

  const toArchive = chosen.filter((r) => settled(r) && !r.archived);
  const toRestore = chosen.filter((r) => r.archived);
  const toDelete = chosen.filter(settled);
  // Sessions an archive or a delete also stops owing notes.
  const owingIn = (list) => list.filter((r) => notesActionable(r) && !isCleared(r)).length;

  const exportChosen = () => {
    // The BOM is what makes Excel on Windows read the file as UTF-8.
    saveBlob(`\ufeff${sessionsCsv(chosen, toExportRow)}`, `${exportName}.csv`, `${CSV_TYPE};charset=utf-8`);
    toast.success(`Exported ${plural(chosen.length, "session")}`);
  };

  const run = async (kind) => {
    const [path, field] = PATHS[kind];
    const targets = kind === "archive" ? toArchive : kind === "restore" ? toRestore : toDelete;
    setBusy(true);
    try {
      const result = await sendInPages(
        targets.map((r) => r.session_key),
        (page) => orgApi.post(path, { session_keys: page }),
        [field],
      );
      const n = result[field].length;
      toastResult(`${plural(n, "session")} ${field}`, n, result.skipped);
      selection.clear();
      setDialog(null);
      onChanged?.();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't update those sessions"));
      if (err.partial) onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const actions = [
    ...notes.actions,
    chosen.length > 0 && { id: "export", label: `Export ${chosen.length}`, icon: "download", onPress: exportChosen },
    toArchive.length > 0 && { id: "archive", label: `Archive ${toArchive.length}`, icon: "archive", onPress: () => setDialog("archive") },
    toRestore.length > 0 && {
      id: "unarchive",
      label: `Restore ${toRestore.length}`,
      icon: "archiveRestore",
      onPress: () => run("restore"),
      disabled: busy,
    },
    toDelete.length > 0 && { id: "delete", label: `Delete ${toDelete.length}`, icon: "trash", danger: true, onPress: () => setDialog("delete") },
  ].filter(Boolean);

  const checkbox = (row) => {
    const key = keyOf(row);
    if (!byKey.has(key)) return null;
    return (
      <SelectBox
        checked={selection.selected.has(key)}
        onChange={() => selection.toggle(key)}
        label={`Select ${row.class_name || row.title || row.display_name || "session"} on ${row.date || row.scheduled_date}`}
        testId={`${testId}-row-${key}`}
      />
    );
  };

  const toolbar = <NotesClearToolbar selection={selection} actions={actions} summary={summary} testId={testId} />;

  const owesLine = (list, verb) => {
    const owing = owingIn(list);
    if (!owing) return null;
    return (
      <p className="font-sans text-[14px] text-[var(--text-secondary)]" data-testid={`${testId}-${verb}-owing`}>
        {owing === 1
          ? `1 of them owes notes: ${verb === "archive" ? "archiving" : "deleting"} it clears the notice, so nobody is asked for them.`
          : `${owing} of them owe notes: ${verb === "archive" ? "archiving" : "deleting"} them clears their notices, so nobody is asked for them.`}
      </p>
    );
  };

  const dialogs = (
    <>
      {notes.dialogs}
      <NotesClearDialog
        open={dialog === "archive"}
        onOpenChange={(open) => !open && setDialog(null)}
        title={`Archive ${plural(toArchive.length, "session")}?`}
        description="They leave your organization's lists. Choose Archived in the status menu to see them again or restore them. They still count in your dashboard's numbers, reports and exports, and instructors keep their notes."
        confirmLabel="Archive"
        busy={busy}
        onConfirm={() => run("archive")}
        testId={`${testId}-archive-dialog`}
      >
        {owesLine(toArchive, "archive")}
      </NotesClearDialog>
      <NotesClearDialog
        open={dialog === "delete"}
        onOpenChange={(open) => !open && setDialog(null)}
        title={`Delete ${plural(toDelete.length, "session")}?`}
        description="They leave your organization's lists and stop counting in its numbers, reports and exports, for good: this can't be undone. Instructors keep their notes, and the class's schedule and billing don't change."
        confirmLabel="Delete"
        danger
        busy={busy}
        onConfirm={() => run("delete")}
        testId={`${testId}-delete-dialog`}
      >
        {owesLine(toDelete, "delete")}
      </NotesClearDialog>
    </>
  );

  return { checkbox, toolbar, dialogs, selection, chosen };
}
